import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';

const MarketControls = ({ 
  selectedContract, 
  onContractChange, 
  priceGrouping, 
  onPriceGroupingChange,
  showAggregated,
  onAggregatedChange,
  connectionStatus,
  onRefresh 
}) => {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(2);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  const contractOptions = [
    { value: 'EUA-DEC24', label: 'EUA December 2024', description: 'European Union Allowances' },
    { value: 'EUA-MAR25', label: 'EUA March 2025', description: 'European Union Allowances' },
    { value: 'EUA-JUN25', label: 'EUA June 2025', description: 'European Union Allowances' },
    { value: 'OIL-JAN25', label: 'Crude Oil January 2025', description: 'WTI Crude Oil Futures' },
    { value: 'OIL-FEB25', label: 'Crude Oil February 2025', description: 'WTI Crude Oil Futures' },
    { value: 'OIL-MAR25', label: 'Crude Oil March 2025', description: 'WTI Crude Oil Futures' },
  ];

  const exchangeOptions = [
    { value: 'ICE', label: 'ICE Futures Europe' },
    { value: 'NYMEX', label: 'NYMEX' },
    { value: 'CME', label: 'CME Group' },
    { value: 'EEX', label: 'European Energy Exchange' }
  ];

  const priceGroupingOptions = [
    { value: '0.01', label: '0.01' },
    { value: '0.05', label: '0.05' },
    { value: '0.10', label: '0.10' },
    { value: '0.25', label: '0.25' },
    { value: '0.50', label: '0.50' },
    { value: '1.00', label: '1.00' }
  ];

  const refreshIntervalOptions = [
    { value: 1, label: '1 second' },
    { value: 2, label: '2 seconds' },
    { value: 5, label: '5 seconds' },
    { value: 10, label: '10 seconds' }
  ];

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        setLastRefresh(new Date());
        onRefresh();
      }, refreshInterval * 1000);
      
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, onRefresh]);

  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return 'text-success';
      case 'connecting': return 'text-warning';
      case 'disconnected': return 'text-error';
      default: return 'text-muted-foreground';
    }
  };

  const getConnectionIcon = () => {
    switch (connectionStatus) {
      case 'connected': return 'Wifi';
      case 'connecting': return 'Loader';
      case 'disconnected': return 'WifiOff';
      default: return 'Wifi';
    }
  };

  const handleManualRefresh = () => {
    setLastRefresh(new Date());
    onRefresh();
  };

  return (
    <div className="bg-card rounded-lg border border-border shadow-subtle">
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-foreground">Market Controls</h3>
          <div className="flex items-center gap-2">
            <Icon 
              name={getConnectionIcon()} 
              size={16} 
              className={`${getConnectionStatusColor()} ${connectionStatus === 'connecting' ? 'animate-spin' : ''}`}
            />
            <span className={`text-sm font-medium ${getConnectionStatusColor()}`}>
              {connectionStatus?.charAt(0)?.toUpperCase() + connectionStatus?.slice(1)}
            </span>
          </div>
        </div>
      </div>
      <div className="p-4 space-y-6">
        {/* Contract Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Select
            label="Contract"
            options={contractOptions}
            value={selectedContract}
            onChange={onContractChange}
            searchable
          />
          
          <Select
            label="Exchange"
            options={exchangeOptions}
            value="ICE"
            onChange={() => {}}
          />
        </div>

        {/* Display Options */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-foreground">Display Options</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Price Grouping"
              options={priceGroupingOptions}
              value={priceGrouping}
              onChange={onPriceGroupingChange}
            />
            
            <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
              <div>
                <label className="text-sm font-medium text-foreground">Aggregated View</label>
                <p className="text-xs text-muted-foreground">Combine orders at same price</p>
              </div>
              <button
                onClick={() => onAggregatedChange(!showAggregated)}
                className={`
                  relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200
                  ${showAggregated ? 'bg-primary' : 'bg-muted'}
                `}
              >
                <span
                  className={`
                    inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200
                    ${showAggregated ? 'translate-x-6' : 'translate-x-1'}
                  `}
                />
              </button>
            </div>
          </div>
        </div>

        {/* Auto Refresh Settings */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-foreground">Refresh Settings</h4>
          
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div>
              <label className="text-sm font-medium text-foreground">Auto Refresh</label>
              <p className="text-xs text-muted-foreground">Automatically update data</p>
            </div>
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`
                relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200
                ${autoRefresh ? 'bg-primary' : 'bg-muted'}
              `}
            >
              <span
                className={`
                  inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200
                  ${autoRefresh ? 'translate-x-6' : 'translate-x-1'}
                `}
              />
            </button>
          </div>

          {autoRefresh && (
            <Select
              label="Refresh Interval"
              options={refreshIntervalOptions}
              value={refreshInterval}
              onChange={setRefreshInterval}
            />
          )}

          <div className="flex items-center justify-between">
            <div className="text-xs text-muted-foreground">
              Last updated: {lastRefresh?.toLocaleTimeString()}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleManualRefresh}
              iconName="RefreshCw"
              iconPosition="left"
            >
              Refresh Now
            </Button>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="space-y-4">
          <h4 className="text-sm font-semibold text-foreground">Quick Actions</h4>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Button
              variant="outline"
              size="sm"
              iconName="ExternalLink"
              iconPosition="left"
              className="justify-start"
            >
              Open Trading Platform
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              iconName="Download"
              iconPosition="left"
              className="justify-start"
            >
              Export Data
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              iconName="Settings"
              iconPosition="left"
              className="justify-start"
            >
              Advanced Settings
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              iconName="Bell"
              iconPosition="left"
              className="justify-start"
            >
              Set Price Alerts
            </Button>
          </div>
        </div>

        {/* Market Status */}
        <div className="p-3 bg-muted/30 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">Market Status</span>
            <span className="text-xs text-success font-semibold">OPEN</span>
          </div>
          <div className="grid grid-cols-2 gap-4 text-xs">
            <div>
              <span className="text-muted-foreground">Session:</span>
              <div className="font-mono text-foreground">Regular Trading</div>
            </div>
            <div>
              <span className="text-muted-foreground">Close:</span>
              <div className="font-mono text-foreground">17:00 GMT</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MarketControls;